import { useMemo, useState } from "react";
import { Grid, Theme, Typography, useMediaQuery } from "@mui/material";

import {
  getDiscountsFromUnitPricing,
  selectIsManualUWApproved,
  selectPolicy,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";

import { PaymentSummaryPricing, StickyContainer } from "@covertree/ui";
import {
  usePaymentPlans,
  type UsePaymentPlansProps,
} from "./hooks/usePaymentPlans";
import {
  CheckoutProvider,
  type CheckoutProviderProps,
} from "./CheckoutProvider";
import { mapAdditionalInterestToFormData } from "./AdditionalInterest";
import { PaymentProcessLeaveModal } from "./PaymentProcessLeaveModal";
import { PaymentMethod } from "./PaymentMethod";
import { PaymentSchedule } from "./PaymentSchedule";
import { PaymentSummary } from "./PaymentSummary";
import { PaymentTerm } from "./PaymentTerm";
import { QuoteDocumentsButtons } from "./QuoteDocumentsButtons";
import { PaymentPlanKey } from "./types";
import { CYQDisclaimer } from "./CYQDisclaimer";
import { BinderDocButton } from "./BinderDocButton";
import { getTotalCostFromPaymentPlan } from "./utils";

type Props = Omit<UsePaymentPlansProps, "policyLocator"> &
  Pick<
    CheckoutProviderProps,
    "isD2C" | "onPaymentSuccess" | "onError" | "isInternalAgent"
  > & {
    isAdditionalInterestsEnabled?: boolean;
    isBinderAvailable?: boolean;
    monthlyPaymentPlanStates?: string;
  };

export function Checkout({
  isD2C,
  onPaymentSuccess,
  isInternalAgent,
  isAdditionalInterestsEnabled = false,
  isBinderAvailable = false,
  monthlyPaymentPlanStates,
  ...props
}: Props) {
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));

  const [selectedPaymentPlanKey, setSelectedPaymentPlanKey] =
    useState<PaymentPlanKey>("annual");

  const { policyLocator, startDate, policyholder } =
    useNewBusinessSelector(selectPolicy)!;
  const isManualUWApproved = useNewBusinessSelector(selectIsManualUWApproved);

  const { forceAgentSignResponsibility, paymentPlans, additionalInterests } =
    usePaymentPlans({
      policyLocator,
      ...props,
    });

  const additionalInterestsFormValues = useMemo(
    () => mapAdditionalInterestToFormData(additionalInterests),
    [additionalInterests]
  );

  const selectedPaymentPlan = useMemo(
    () => paymentPlans?.[selectedPaymentPlanKey],
    [selectedPaymentPlanKey, paymentPlans]
  );

  const pricing: PaymentSummaryPricing = useMemo(() => {
    if (!selectedPaymentPlan) {
      return {
        discounts: [],
        totalCost: 0,
        summary: [],
      };
    }

    const paymentPlanPricing = selectedPaymentPlan.pricing;

    const { discounts, discountsTotal } =
      getDiscountsFromUnitPricing(paymentPlanPricing);

    const totalCost = getTotalCostFromPaymentPlan(selectedPaymentPlan);

    const { fees, taxes } = selectedPaymentPlan.installments.reduce(
      (acc, installment) => ({
        fees: acc.fees + installment.fees,
        taxes: acc.taxes + installment.taxes,
      }),
      { fees: 0, taxes: 0 }
    );

    const firstInstallment = selectedPaymentPlan.installments[0];

    const dueToday =
      firstInstallment.premium + firstInstallment.fees + firstInstallment.taxes;

    return {
      discounts,
      totalCost,
      dueToday,
      summary: [
        {
          name: "Starting Premium",
          value: paymentPlanPricing.grossPremium + discountsTotal,
        },
        {
          name: "Fees",
          value: parseFloat(fees.toFixed(2)),
        },
        {
          name: "Taxes",
          value: parseFloat(taxes.toFixed(2)),
        },
      ],
    };
  }, [selectedPaymentPlan]);

  if (!paymentPlans || !selectedPaymentPlan) {
    return null;
  }

  const paymentSummary = (
    <PaymentSummary policyStartDate={startDate} pricing={pricing} />
  );

  const quoteDocumentsButtons = (
    <QuoteDocumentsButtons policyLocator={policyLocator} />
  );

  const paymentSchedule = (
    <PaymentSchedule installments={selectedPaymentPlan?.installments} />
  );

  const binderDocButton = isBinderAvailable ? <BinderDocButton /> : null;

  return (
    <CheckoutProvider
      forceAgentSignResponsibility={forceAgentSignResponsibility}
      isD2C={isD2C}
      policyLocator={policyLocator}
      onError={props.onError}
      onPaymentSuccess={onPaymentSuccess}
      selectedPaymentPlanKey={selectedPaymentPlanKey}
      isAdditionalInterestsEnabled={isAdditionalInterestsEnabled}
      initialAdditionalInterests={additionalInterestsFormValues}
      monthlyPaymentPlanStates={monthlyPaymentPlanStates}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          {!isD2C && <Typography variant="h3">Checkout</Typography>}
          {isManualUWApproved && (
            <CYQDisclaimer isInternalAgent={isInternalAgent} />
          )}
          <PaymentTerm
            onPaymentTermSelect={setSelectedPaymentPlanKey}
            paymentPlans={paymentPlans}
            selectedPaymentPlanKey={selectedPaymentPlanKey}
          />
          {!isDesktop && paymentSummary}
          {!isDesktop && quoteDocumentsButtons}
          {!isDesktop && paymentSchedule}
          {!isDesktop && binderDocButton}
          <PaymentMethod
            mortgageBillAvailable={!!paymentPlans.mortgageBill?.isAvailable}
            selectedPaymentPlanKey={selectedPaymentPlanKey}
            onPaymentMethodChange={setSelectedPaymentPlanKey}
            selectedPaymentPlan={selectedPaymentPlan}
            policyholder={policyholder}
            pricing={pricing}
            isD2C={isD2C}
          />
        </Grid>
        {isDesktop && (
          <Grid item xs={4} container>
            <Grid item xs={12} justifyContent="center">
              <StickyContainer topOffset={isD2C ? 16 : 120} isD2C={isD2C}>
                {paymentSummary}
                {quoteDocumentsButtons}
                {paymentSchedule}
                {binderDocButton}
              </StickyContainer>
            </Grid>
          </Grid>
        )}
      </Grid>
      <PaymentProcessLeaveModal />
    </CheckoutProvider>
  );
}
